import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CollapsibleSection from '@/components/superuser/CollapsibleSection';
import type { DebugDocument } from '@/types/superUserDebug.ts';

interface Props {
  documents: DebugDocument[];
}

export default function DocumentsPanel({ documents }: Props) {
  const { t } = useTranslation();

  return (
    <CollapsibleSection
      title={`${t('superuser.debug.detail.docs.title', 'Documentos de la simulación')} (${documents.length})`}
    >
      {documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('superuser.debug.detail.docs.empty', 'La simulación no tiene documentos asociados.')}
        </p>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <DocumentItem key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}

function DocumentItem({ doc }: { doc: DebugDocument }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [extractedOpen, setExtractedOpen] = useState(false);

  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
        <FileText className="size-4 shrink-0 text-muted-foreground" />
        <span className="font-medium">{doc.fileName}</span>
        <Badge variant="outline" className="font-mono text-[10px] uppercase">
          {t(`superuser.debug.detail.docs.status.${doc.processingStatus}`, doc.processingStatus)}
        </Badge>
        {doc.failureReason && (
          <span className="text-xs text-destructive">· {doc.failureReason}</span>
        )}
      </button>

      {open && (
        <div className="space-y-3 px-3 pb-3 text-sm">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('superuser.debug.detail.docs.summary', 'Summary (briefing)')}
            </p>
            {doc.summary ? (
              <pre className="max-h-[240px] overflow-auto rounded-md border bg-card p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
                {doc.summary}
              </pre>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('superuser.debug.detail.docs.summaryEmpty', 'Sin summary persistido.')}
              </p>
            )}
          </div>

          <div>
            <button
              type="button"
              onClick={() => setExtractedOpen((o) => !o)}
              className="flex w-full items-center gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              {extractedOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
              {t('superuser.debug.detail.docs.extracted', 'Extracted text (evaluator)')}
            </button>
            {extractedOpen && (
              <>
                {doc.extractedText ? (
                  <pre className="mt-2 max-h-[360px] overflow-auto rounded-md border bg-card p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
                    {doc.extractedText}
                  </pre>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('superuser.debug.detail.docs.extractedEmpty', 'Sin extractedText persistido.')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
